import { Component, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';

import { FaviconsService } from '@zonar-ui/core';
import { GTCxMobileHelperService } from '@zonar-ui/gtcx-mobile-helper';

import { appName } from '@environments/shared';
import {
  SidenavFooterConfig,
  SidenavGlobalConfig,
  SidenavHeaderConfig,
  SidenavMenuConfig
} from '@config/sidenav.config';
import { UserCompany } from '@app/models/user.company.model';
import { DataDogService } from './services/data-dog/data-dog.service';
import { SelectedCompanyService } from './services/selected-company/selected-company.service';
import { DeviceDetectionService } from './services/device-detection/device-detection.service';
import { PendoService } from '@zonar-ui/analytics';
import { PermissionsService } from '@zonar-ui/auth';
import { IDivisionMap } from '@zonar-ui/auth/lib/models/company.model';
import { FeatureToggleService } from '@zonar-ui/feature-toggle';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  private onDestroy$ = new Subject<void>();
  loading = true;
  title = appName;
  selectedCompany: UserCompany = null;
  selectedCompanyId$ = new BehaviorSubject<UserCompany['value']>(null);
  sidenavConfig = SidenavGlobalConfig;
  sidenavHeader = SidenavHeaderConfig;

  constructor(
    private auth: AuthService,
    private datadog: DataDogService,
    private favicon: FaviconsService,
    private selectedCompanyService: SelectedCompanyService,
    private router: Router,
    public device: DeviceDetectionService,
    private permissionsService: PermissionsService,
    private featureToggleService: FeatureToggleService,
    private pendoService: PendoService,
    private gtcxMobileHelperService: GTCxMobileHelperService
  ) {
    this.auth.isAuthenticated$
      .pipe(filter((isAuthenticated) => Boolean(isAuthenticated)))
      .subscribe(() => {
        this.datadog.startSessionReplayRecording();
      });

    this.router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loading = true;
          break;
        }
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loading = false;
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  ngOnInit(): void {
    this.permissionsService
      .getPermissions()
      .pipe(
        takeUntil(this.onDestroy$),
        debounceTime(250),
        filter((permissions) => Boolean(permissions))
      )
      .subscribe(() => {
        this.pendoService.initialize();
      });
  }

  switchToSelectedCompany(incomingCompany: UserCompany): void {
    // This is needed until fix is applied to sidenav component.
    this.permissionsService
      .getCompanyMap()
      .pipe(
        take(1),
        map((companies) => {
          if (companies && companies[incomingCompany.value]) {
            // Setting Company context to use it on Zonar-Owner-Id for selecting right profile.
            this.permissionsService.setCurrentCompanyContext(
              companies[incomingCompany.value]
            );
          }
          this.selectedCompanyService.setCompanyIdFromSideNav(incomingCompany);
          this.selectedCompanyService.setUserCompany(incomingCompany.value);
          this.featureToggleService.initializeDevCycle(incomingCompany.value);
          this.gtcxMobileHelperService.setCurrentCompany(incomingCompany.value);
        })
      )
      .subscribe();
  }

  onSideNavMobileMenuButtonToggled(event): void {
    this.sidenavConfig.mobileOpened = event;
  }
}
